import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from '../config';
import { useUser } from '../contexts/UserContext';
import './IdeaCheckModal.css';

interface SimilarInitiative {
  id: number;
  use_case_name: string;
  purpose: string;
  proposed_solution_owner: string;
  similarities?: string[];
  differences?: string[];
}

interface InitiativeDetail {
  id: number;
  use_case_name: string;
  purpose?: string;
  value?: string;
  solution_scope?: string;
  proposed_workflow?: string;
  inputs_and_data_sources?: string;
  proposed_solution_owner?: string;
  solution_sponsor?: string;
  division?: string;
  department?: string;
  status?: string;
  users?: string;
}

interface Props {
  onProceed: (prefill?: Record<string, string>) => void;
  onCancel: () => void;
}

type Step = 'input' | 'checking' | 'results';

// Collapses hard-wrapped lines back into paragraphs and drops repeated blank lines.
const cleanParagraphs = (text?: string): string[] => {
  if (!text) return [];
  return text
    .replace(/\r\n/g, '\n')
    .split(/\n\s*\n+/)
    .map(block => block.split('\n').map(line => line.trim()).filter(Boolean).join(' '))
    .filter(Boolean);
};

const IdeaCheckModal: React.FC<Props> = ({ onProceed, onCancel }) => {
  const { user } = useUser();
  const [idea, setIdea] = useState('');
  const [step, setStep] = useState<Step>('input');
  const [similar, setSimilar] = useState<SimilarInitiative[]>([]);
  const [detail, setDetail] = useState<InitiativeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [similarityTarget, setSimilarityTarget] = useState<SimilarInitiative | null>(null);
  const [similarities, setSimilarities] = useState<string[]>([]);
  const [differences, setDifferences] = useState<string[]>([]);
  const [similarityError, setSimilarityError] = useState('');
  const [similarityLoading, setSimilarityLoading] = useState(false);
  const [similarityModalOpen, setSimilarityModalOpen] = useState(false);
  const [prefilling, setPrefilling] = useState(false);
  // engagement state: { [initiativeId]: { liked, partnered, likeCount, partnerCount } }
  const [engagements, setEngagements] = useState<Record<number, { liked: boolean; partnered: boolean; likeCount: number; partnerCount: number; partnerMessage: string }>>({});
  const [engagementLoading, setEngagementLoading] = useState<Record<number, { like?: boolean; partner?: boolean }>>({});
  const [partnerModalId, setPartnerModalId] = useState<number | null>(null);
  const [partnerMessage, setPartnerMessage] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (partnerModalId !== null) {
        setPartnerModalId(null);
      } else if (detailModalOpen) {
        setDetailModalOpen(false);
      } else if (similarityModalOpen) {
        setSimilarityModalOpen(false);
      } else {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [partnerModalId, detailModalOpen, similarityModalOpen, onCancel]);

  const handleProceed = async () => {
    setPrefilling(true);
    try {
      const res = await axios.post(api('/api/initiatives/prefill-from-idea'), { idea });
      onProceed(res.data.fields || {});
    } catch {
      onProceed({});
    } finally {
      setPrefilling(false);
    }
  };

  const handleCheck = async () => {
    if (!idea.trim()) return;
    setStep('checking');
    try {
      const res = await axios.post(api('/api/initiatives/check-duplicate'), { idea });
      const found: SimilarInitiative[] = res.data.similar || [];
      setSimilar(found);
      // Load engagement data for found initiatives
      if (found.length > 0) {
        const ids = found.map(s => s.id).join(',');
        try {
          const [summaryRes, myRes] = await Promise.all([
            axios.get(api(`/api/initiatives/engagements/summary?initiative_ids=${ids}`)),
            user?.email ? axios.get(api(`/api/initiatives/engagements/my?user_email=${encodeURIComponent(user.email)}`)) : Promise.resolve({ data: {} })
          ]);
          const summary = summaryRes.data as Record<string, { like_count: number; partner_count: number }>;
          const mine = myRes.data as Record<string, { liked: boolean; partnered: boolean }>;
          const initial: typeof engagements = {};
          found.forEach(s => {
            const key = String(s.id);
            initial[s.id] = {
              liked: mine[key]?.liked || false,
              partnered: mine[key]?.partnered || false,
              likeCount: summary[key]?.like_count || 0,
              partnerCount: summary[key]?.partner_count || 0,
              partnerMessage: ''
            };
          });
          setEngagements(initial);
        } catch { /* silent */ }
      }
      setStep('results');
    } catch {
      setStep('results');
      setSimilar([]);
    }
  };

  const handleToggleLike = async (initiativeId: number) => {
    if (!user?.email) return;
    const current = engagements[initiativeId];
    const isLiked = current?.liked;
    setEngagementLoading(prev => ({ ...prev, [initiativeId]: { ...prev[initiativeId], like: true } }));
    try {
      if (isLiked) {
        await axios.delete(api(`/api/initiatives/${initiativeId}/like`), { data: { current_user_email: user.email } });
        setEngagements(prev => ({ ...prev, [initiativeId]: { ...prev[initiativeId], liked: false, likeCount: Math.max(0, (prev[initiativeId]?.likeCount || 1) - 1) } }));
      } else {
        await axios.post(api(`/api/initiatives/${initiativeId}/like`), { current_user_email: user.email });
        setEngagements(prev => ({ ...prev, [initiativeId]: { ...prev[initiativeId], liked: true, likeCount: (prev[initiativeId]?.likeCount || 0) + 1 } }));
      }
    } catch { /* silent */ }
    setEngagementLoading(prev => ({ ...prev, [initiativeId]: { ...prev[initiativeId], like: false } }));
  };

  const handleSubmitPartner = async (initiativeId: number) => {
    if (!user?.email) return;
    const current = engagements[initiativeId];
    if (current?.partnered) {
      // withdraw
      setEngagementLoading(prev => ({ ...prev, [initiativeId]: { ...prev[initiativeId], partner: true } }));
      try {
        await axios.delete(api(`/api/initiatives/${initiativeId}/partner-request`), { data: { current_user_email: user.email } });
        setEngagements(prev => ({ ...prev, [initiativeId]: { ...prev[initiativeId], partnered: false, partnerCount: Math.max(0, (prev[initiativeId]?.partnerCount || 1) - 1) } }));
      } catch { /* silent */ }
      setEngagementLoading(prev => ({ ...prev, [initiativeId]: { ...prev[initiativeId], partner: false } }));
      return;
    }
    // open partner message modal
    setPartnerMessage('');
    setPartnerModalId(initiativeId);
  };

  const handleConfirmPartner = async () => {
    if (!user?.email || partnerModalId === null) return;
    setEngagementLoading(prev => ({ ...prev, [partnerModalId]: { ...prev[partnerModalId], partner: true } }));
    try {
      await axios.post(api(`/api/initiatives/${partnerModalId}/partner-request`), { current_user_email: user.email, message: partnerMessage });
      setEngagements(prev => ({ ...prev, [partnerModalId!]: { ...prev[partnerModalId!], partnered: true, partnerCount: (prev[partnerModalId!]?.partnerCount || 0) + 1 } }));
    } catch { /* silent */ }
    setEngagementLoading(prev => ({ ...prev, [partnerModalId!]: { ...prev[partnerModalId!], partner: false } }));
    setPartnerModalId(null);
  };

  const handleViewDetail = async (id: number) => {
    setDetailLoading(true);
    setDetail(null);
    setDetailModalOpen(true);
    try {
      const res = await axios.get(api(`/api/initiatives/${id}`));
      setDetail(res.data);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleFindSimilarity = async (s: SimilarInitiative) => {
    setSimilarityTarget(s);
    setSimilarityError('');
    setSimilarityModalOpen(true);
    // check-duplicate already computed this analysis in the same AI call — reuse it if non-empty, otherwise fall back to a dedicated call.
    if ((s.similarities && s.similarities.length > 0) || (s.differences && s.differences.length > 0)) {
      setSimilarities(s.similarities || []);
      setDifferences(s.differences || []);
      setSimilarityLoading(false);
      return;
    }
    setSimilarities([]);
    setDifferences([]);
    setSimilarityLoading(true);
    try {
      const res = await axios.post(api('/api/initiatives/explain-similarity'), { idea, initiative_id: s.id });
      setSimilarities(res.data.similarities || []);
      setDifferences(res.data.differences || []);
      if (res.data.error) setSimilarityError(res.data.error);
    } catch {
      setSimilarityError('Could not generate analysis. Please try again.');
    } finally {
      setSimilarityLoading(false);
    }
  };

  return (
    <>
    <div className="idea-check-inline">

        {step === 'input' && (
          <>
            <h2 className="idea-check-title">Describe Your Idea</h2>
            <p className="idea-check-sub">
              Before submitting, briefly describe your AI initiative idea so we can check for similar existing submissions.
            </p>
            <textarea
              className="idea-check-textarea"
              placeholder="e.g. Automate invoice matching using AI to reduce manual effort in finance..."
              value={idea}
              onChange={e => setIdea(e.target.value)}
              rows={5}
              autoFocus
            />
            <div className="idea-check-actions">
              <button className="idea-check-btn idea-check-btn--secondary" onClick={onCancel}>← Go Back</button>
              <button className="idea-check-btn idea-check-btn--primary" onClick={handleCheck} disabled={!idea.trim()}>
                Check for Similar Ideas
              </button>
            </div>
          </>
        )}

        {step === 'checking' && (
          <div className="idea-check-checking">
            <div className="idea-check-spinner" />
            <p>Checking for similar initiatives...</p>
          </div>
        )}

        {step === 'results' && similar.length === 0 && (
          <>
            <h2 className="idea-check-title">No Similar Initiatives Found</h2>
            <p className="idea-check-sub">
              Great news! No similar approved initiatives were found. You can proceed with your submission.
            </p>
            <div className="idea-check-actions">
              <button className="idea-check-btn idea-check-btn--secondary" onClick={onCancel}>← Go Back</button>
              <button className="idea-check-btn idea-check-btn--primary" onClick={() => onProceed({})}>
                Create New
              </button>
            </div>
          </>
        )}

        {step === 'results' && similar.length > 0 && (
          <>
            <h2 className="idea-check-title">Similar Initiatives Found</h2>
            <p className="idea-check-sub">
              The following approved initiatives appear to be similar to your idea. Please review them before proceeding.
            </p>
            <div className="idea-check-similar-list">
              {similar.map(s => {
                const eng = engagements[s.id] || { liked: false, partnered: false, likeCount: 0, partnerCount: 0 };
                const loadingLike = engagementLoading[s.id]?.like;
                const loadingPartner = engagementLoading[s.id]?.partner;
                return (
                  <div key={s.id} className="idea-check-similar-item">
                    <div className="idea-check-similar-header">
                      <div className="idea-check-similar-title">{s.use_case_name}</div>
                      <div className="idea-check-similar-actions">
                        <button className="idea-check-view-btn" onClick={() => handleViewDetail(s.id)}>View Details →</button>
                        <button className="idea-check-view-btn idea-check-view-btn--compare" onClick={() => handleFindSimilarity(s)}>Find Similarities</button>
                      </div>
                    </div>
                    <div className="idea-check-similar-owner">Owner: {s.proposed_solution_owner}</div>
                    {s.purpose && <div className="idea-check-similar-purpose">{s.purpose}</div>}
                    {user?.email && (
                      <div className="idea-check-engage-row">
                        <button
                          className={`idea-check-engage-btn${eng.liked ? ' idea-check-engage-btn--active' : ''}`}
                          onClick={() => handleToggleLike(s.id)}
                          disabled={!!loadingLike}
                          title={eng.liked ? 'Unlike this initiative' : 'Like this initiative'}
                        >
                          👍 {eng.liked ? 'Liked' : 'Like'}{eng.likeCount > 0 ? ` (${eng.likeCount})` : ''}
                        </button>
                        <button
                          className={`idea-check-engage-btn${eng.partnered ? ' idea-check-engage-btn--active idea-check-engage-btn--partner' : ' idea-check-engage-btn--partner-outline'}`}
                          onClick={() => handleSubmitPartner(s.id)}
                          disabled={!!loadingPartner}
                          title={eng.partnered ? 'Withdraw partnership request' : 'Request partnership'}
                        >
                          🤝 {eng.partnered ? 'Partnership Requested' : 'Request Partnership'}{eng.partnerCount > 0 ? ` (${eng.partnerCount})` : ''}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="idea-check-confirm-text">
              Do you still want to submit a new initiative?
            </p>
            <div className="idea-check-actions">
              <button className="idea-check-btn idea-check-btn--secondary" onClick={onCancel}>
                ← Go Back
              </button>
              <button className="idea-check-btn idea-check-btn--primary" onClick={handleProceed} disabled={prefilling}>
                {prefilling ? 'Preparing form...' : 'Yes, Submit Anyway'}
              </button>
            </div>
          </>
        )}

    </div>

    {partnerModalId !== null && (
      <div className="idea-check-overlay" style={{ zIndex: 2000 }}>
        <div className="idea-check-modal" style={{ maxWidth: 420 }}>
          <h2 className="idea-check-title">Request Partnership</h2>
          <p className="idea-check-sub">Optionally add a message to the initiative owner about how you'd like to collaborate.</p>
          <textarea
            className="idea-check-textarea"
            placeholder="e.g. Our team has relevant data that could complement this initiative..."
            value={partnerMessage}
            onChange={e => setPartnerMessage(e.target.value)}
            rows={4}
          />
          <div className="idea-check-actions">
            <button className="idea-check-btn idea-check-btn--secondary" onClick={() => setPartnerModalId(null)}>Cancel</button>
            <button className="idea-check-btn idea-check-btn--primary" onClick={handleConfirmPartner} disabled={engagementLoading[partnerModalId]?.partner}>
              {engagementLoading[partnerModalId]?.partner ? 'Submitting...' : 'Send Request'}
            </button>
          </div>
        </div>
      </div>
    )}

    {detailModalOpen && (
      <div className="idea-check-overlay" onClick={() => setDetailModalOpen(false)}>
        <div className="idea-check-modal idea-check-popup" onClick={e => e.stopPropagation()}>
          <button className="idea-check-popup-close" onClick={() => setDetailModalOpen(false)} aria-label="Close">✕</button>
          {detailLoading && (
            <div className="idea-check-checking">
              <div className="idea-check-spinner" />
              <p>Loading initiative details...</p>
            </div>
          )}
          {!detailLoading && detail && (
            <div className="idea-check-detail">
              <h2 className="idea-check-title">{detail.use_case_name}</h2>
              {detail.status && <span className="idea-check-detail-status">{detail.status}</span>}
              <div className="idea-check-detail-fields">
                {detail.division && <div className="idea-check-detail-row"><span className="idea-check-detail-label">Division</span><span>{detail.division}</span></div>}
                {detail.department && <div className="idea-check-detail-row"><span className="idea-check-detail-label">Department</span><span>{detail.department}</span></div>}
                {detail.proposed_solution_owner && <div className="idea-check-detail-row"><span className="idea-check-detail-label">Owner</span><span>{detail.proposed_solution_owner}</span></div>}
                {detail.solution_sponsor && <div className="idea-check-detail-row"><span className="idea-check-detail-label">Sponsor</span><span>{detail.solution_sponsor}</span></div>}
                {detail.purpose && <div className="idea-check-detail-row idea-check-detail-row--block"><span className="idea-check-detail-label">Purpose</span>{cleanParagraphs(detail.purpose).map((p, i) => <p key={i}>{p}</p>)}</div>}
                {detail.value && <div className="idea-check-detail-row idea-check-detail-row--block"><span className="idea-check-detail-label">Value</span>{cleanParagraphs(detail.value).map((p, i) => <p key={i}>{p}</p>)}</div>}
                {detail.solution_scope && <div className="idea-check-detail-row idea-check-detail-row--block"><span className="idea-check-detail-label">Scope</span>{cleanParagraphs(detail.solution_scope).map((p, i) => <p key={i}>{p}</p>)}</div>}
                {detail.proposed_workflow && <div className="idea-check-detail-row idea-check-detail-row--block"><span className="idea-check-detail-label">Proposed Workflow</span>{cleanParagraphs(detail.proposed_workflow).map((p, i) => <p key={i}>{p}</p>)}</div>}
                {detail.inputs_and_data_sources && <div className="idea-check-detail-row idea-check-detail-row--block"><span className="idea-check-detail-label">Inputs & Data Sources</span>{cleanParagraphs(detail.inputs_and_data_sources).map((p, i) => <p key={i}>{p}</p>)}</div>}
                {detail.users && <div className="idea-check-detail-row idea-check-detail-row--block"><span className="idea-check-detail-label">Users</span>{cleanParagraphs(detail.users).map((p, i) => <p key={i}>{p}</p>)}</div>}
              </div>
            </div>
          )}
          {!detailLoading && !detail && (
            <p className="idea-check-sub">Could not load initiative details.</p>
          )}
        </div>
      </div>
    )}

    {similarityModalOpen && (
      <div className="idea-check-overlay" onClick={() => setSimilarityModalOpen(false)}>
        <div className="idea-check-modal idea-check-popup" onClick={e => e.stopPropagation()}>
          <button className="idea-check-popup-close" onClick={() => setSimilarityModalOpen(false)} aria-label="Close">✕</button>
          <h2 className="idea-check-title">AI Similarity Analysis</h2>
          {similarityTarget && (
            <div className="idea-check-similarity-compare">
              <div className="idea-check-similarity-side">
                <div className="idea-check-similarity-side-label">Your Idea</div>
                <p>{idea}</p>
              </div>
              <div className="idea-check-similarity-vs">vs</div>
              <div className="idea-check-similarity-side">
                <div className="idea-check-similarity-side-label">Existing Initiative</div>
                <p>{similarityTarget.use_case_name}</p>
              </div>
            </div>
          )}
          {similarityLoading && (
            <div className="idea-check-checking">
              <div className="idea-check-spinner" />
              <p>Analysing similarities...</p>
            </div>
          )}
          {!similarityLoading && similarityError && (
            <p className="idea-check-sub">
              Could not generate analysis.{' '}
              <a
                href="#retry"
                className="idea-check-error-link"
                onClick={e => { e.preventDefault(); if (similarityTarget) handleFindSimilarity(similarityTarget); }}
              >
                Please try again
              </a>.
            </p>
          )}
          {!similarityLoading && !similarityError && (
            <div className="idea-check-analysis-grid">
              <div className="idea-check-analysis-col idea-check-analysis-col--similar">
                <div className="idea-check-analysis-col-label">
                  <span className="idea-check-analysis-icon idea-check-analysis-icon--similar">✓</span>
                  Similarities
                </div>
                {similarities.length === 0 && <p className="idea-check-analysis-empty">No notable similarities identified.</p>}
                <ul className="idea-check-analysis-list">
                  {similarities.map((s, i) => <li key={i}>{s}</li>)}
                </ul>
              </div>
              <div className="idea-check-analysis-col idea-check-analysis-col--diff">
                <div className="idea-check-analysis-col-label">
                  <span className="idea-check-analysis-icon idea-check-analysis-icon--diff">≠</span>
                  Differences
                </div>
                {differences.length === 0 && <p className="idea-check-analysis-empty">No notable differences identified.</p>}
                <ul className="idea-check-analysis-list">
                  {differences.map((d, i) => <li key={i}>{d}</li>)}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
};

export default IdeaCheckModal;
